import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import {
  CreateDriverRequest,
  DriverCompanyOption,
  DriverDetailResponse,
  DriverGroupOption,
  DriverStatus
} from '../../core/models/portal.models';

type DriverEditForm = CreateDriverRequest & {
  accessActive: boolean;
};

@Component({
  selector: 'app-driver-edit-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './driver-edit-page.component.html'
})
export class DriverEditPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly detail = signal<DriverDetailResponse | null>(null);
  protected readonly companies = signal<DriverCompanyOption[]>([]);
  protected readonly driverGroups = signal<DriverGroupOption[]>([]);
  protected readonly form = signal<DriverEditForm>(this.emptyForm());
  protected readonly saving = signal(false);
  protected readonly saveError = signal<string | null>(null);
  private driverId = 0;

  ngOnInit(): void {
    this.driverId = Number(this.route.snapshot.paramMap.get('id'));
    this.api.driver(this.driverId).subscribe((detail) => {
      this.detail.set(detail);
      this.companies.set(detail.companies);
      this.form.set(this.formFromDetail(detail));
      this.api.driverGroups().subscribe((groups) => this.driverGroups.set(groups));
    });
  }

  protected save(event: Event): void {
    event.preventDefault();
    const request = this.normalizedRequest();

    if (!request.firstName || !request.lastName || !request.email || !request.companyId) {
      this.saveError.set('Bitte fülle Vorname, Nachname, E-Mail und Betrieb aus.');
      return;
    }

    this.saving.set(true);
    this.saveError.set(null);

    this.api.updateDriver(this.driverId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.router.navigate(['/drivers', this.driverId]);
      },
      error: () => {
        this.saving.set(false);
        this.saveError.set('Der Fahrer konnte nicht gespeichert werden.');
      }
    });
  }

  protected updateForm(field: keyof Omit<DriverEditForm, 'companyId' | 'accessActive'>, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement).value;
    this.form.update((current) => ({ ...current, [field]: value }));
  }

  protected updateCompany(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    this.form.update((current) => ({ ...current, companyId: value, groupIds: [] }));
  }

  protected updateAccess(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.form.update((current) => ({ ...current, accessActive: checked }));
  }

  protected updateGroups(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const groupIds = Array.from(select.selectedOptions)
      .map((option) => Number(option.value))
      .filter((value) => Number.isFinite(value));
    this.form.update((current) => ({ ...current, groupIds }));
  }

  protected driverGroupOptions(): DriverGroupOption[] {
    return this.driverGroups().filter((group) => group.companyId === this.form().companyId);
  }

  private formFromDetail(detail: DriverDetailResponse): DriverEditForm {
    return {
      firstName: detail.driver.firstName ?? '',
      lastName: detail.driver.lastName ?? '',
      email: detail.driver.email,
      phone: detail.driver.phone,
      personnelNumber: detail.driver.personnelNumber,
      companyId: detail.driver.companyId,
      preferredLanguage: detail.driver.preferredLanguage,
      dateOfBirth: null,
      licenseValidUntil: detail.driver.licenseValidUntil,
      pScheinValidUntil: detail.driver.pScheinValidUntil,
      statusValidUntil: detail.driver.statusValidUntil,
      status: detail.driver.status,
      groupIds: detail.groupIds,
      accessActive: detail.driver.accessActive
    };
  }

  private normalizedRequest(): CreateDriverRequest {
    const current = this.form();
    return {
      firstName: this.clean(current.firstName) ?? '',
      lastName: this.clean(current.lastName) ?? '',
      email: this.clean(current.email)?.toLowerCase() ?? '',
      phone: this.clean(current.phone),
      personnelNumber: this.clean(current.personnelNumber),
      companyId: current.companyId,
      groupIds: current.groupIds,
      preferredLanguage: this.clean(current.preferredLanguage) ?? 'de',
      dateOfBirth: this.clean(current.dateOfBirth),
      licenseValidUntil: this.clean(current.licenseValidUntil),
      pScheinValidUntil: this.clean(current.pScheinValidUntil),
      accessActive: current.accessActive
    };
  }

  private emptyForm(): DriverEditForm {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      personnelNumber: '',
      companyId: 0,
      groupIds: [],
      preferredLanguage: 'de',
      dateOfBirth: null,
      licenseValidUntil: null,
      pScheinValidUntil: null,
      statusValidUntil: null,
      status: 'ACTIVE',
      accessActive: true
    };
  }

  private clean(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
